/**
 * Client-side logger that saves logs to Supabase for debugging
 * Logs are stored in sessionStorage and can be sent to Supabase
 */

import { getSupabase } from './supabase';
import { reportError } from './errorReporter';

export interface LogEntry {
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'debug';
  message: string;
  data?: Record<string, unknown>;
  context?: string;
}

const LOG_STORAGE_KEY = 'genbasafeai_client_logs';
const MAX_LOGS = 100;

// Get all stored logs
export function getStoredLogs(): LogEntry[] {
  try {
    const stored = sessionStorage.getItem(LOG_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

// Add a log entry
function addLog(entry: LogEntry): void {
  try {
    const logs = getStoredLogs();
    logs.push(entry);
    // Keep only the last MAX_LOGS entries
    const trimmedLogs = logs.slice(-MAX_LOGS);
    sessionStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(trimmedLogs));
  } catch (e) {
    console.error('Failed to store log:', e);
  }
}

// Clear all logs
export function clearLogs(): void {
  try {
    sessionStorage.removeItem(LOG_STORAGE_KEY);
  } catch {
    // Ignore
  }
}

// Log functions
export function logInfo(message: string, data?: Record<string, unknown>, context?: string): void {
  const entry: LogEntry = {
    timestamp: new Date().toISOString(),
    level: 'info',
    message,
    data,
    context
  };
  addLog(entry);
  console.log(`[INFO] ${message}`, data);
}

export function logWarn(message: string, data?: Record<string, unknown>, context?: string): void {
  const entry: LogEntry = {
    timestamp: new Date().toISOString(),
    level: 'warn',
    message,
    data,
    context
  };
  addLog(entry);
  console.warn(`[WARN] ${message}`, data);
}

export function logError(message: string, data?: Record<string, unknown>, context?: string): void {
  const entry: LogEntry = {
    timestamp: new Date().toISOString(),
    level: 'error',
    message,
    data,
    context
  };
  addLog(entry);
  console.error(`[ERROR] ${message}`, data);
  /* localStorage に積むだけでは、利用者の端末から出てこない。
     ここは既に「残す価値がある」と判断された地点なので、サーバーにも送る。
     data は呼び出し側が組んだ診断用の値で、報告の本文は入っていない。 */
  reportError('flow', message, context);
}

export function logDebug(message: string, data?: Record<string, unknown>, context?: string): void {
  const entry: LogEntry = {
    timestamp: new Date().toISOString(),
    level: 'debug',
    message,
    data,
    context
  };
  addLog(entry);
  console.log(`[DEBUG] ${message}`, data);
}

/* sendLogsToServer は削除した。呼んでいた genbasafeai_store-logs は
   **そもそも存在せず**、しかもこの関数はどこからも呼ばれていなかった。
   サーバーへの送信は services/errorReporter.ts に一本化する。 */

// Format logs for display
export function formatLogsForDisplay(): string {
  const logs = getStoredLogs();
  return logs.map(log => {
    const dataStr = log.data ? ` | ${JSON.stringify(log.data)}` : '';
    return `[${log.timestamp}] [${log.level.toUpperCase()}] ${log.message}${dataStr}`;
  }).join('\n');
}

// Get summary of recent activity
export function getLogSummary(): {
  total: number;
  errors: number;
  warnings: number;
  lastError?: LogEntry;
} {
  const logs = getStoredLogs();
  const errors = logs.filter(l => l.level === 'error');
  const warnings = logs.filter(l => l.level === 'warn');

  return {
    total: logs.length,
    errors: errors.length,
    warnings: warnings.length,
    lastError: errors[errors.length - 1]
  };
}
